import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { AppButton } from "site/ui";
import { BooleanLiteral } from "utils/types";

const DropdownMenu = styled.div`
  position: absolute;
  top: 40px;
  left: 0;
  width: 180px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
`;

const MenuItem = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &.delete {
    color: #ff4d4f;
  }
`;

const ModalIcon = styled(ExclamationCircleFilled)`
  font-size: 50px;
  color: #faad14;
  margin-right: 8px;
  margin-bottom: 10px;
  font-family: "Manrope", sans-serif;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 4px;
  text-align: center;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: center;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0;
  font-family: "Manrope", sans-serif;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;

export interface CourseMenuProps {
  isHidden: boolean;
  isOpen: boolean;
  onClose: () => void;
  onEditDetails: () => void;
  onAddQuestions: () => void;
  onHideCourse: (shouldHide: BooleanLiteral) => void;
  onDeleteCourse: () => void;
}

export const CourseMenu: React.FC<CourseMenuProps> = ({
  isOpen,
  onClose,
  onEditDetails,
  onAddQuestions,
  onHideCourse,
  onDeleteCourse,
  isHidden
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <DropdownMenu ref={menuRef}>
      <MenuItem onClick={e => {
        e.stopPropagation(); 
        onEditDetails()
      }}>Edit details</MenuItem>
      <MenuItem onClick={(e) => {
        e.stopPropagation(); 
        onAddQuestions()
      }}>
        Add past questions
      </MenuItem>
      <MenuItem onClick={(e) => {
        e.stopPropagation(); 
        onHideCourse(isHidden ? "false" : "true")
      }}>{isHidden ? "Show " : "Hide "} course</MenuItem>
      <MenuItem className="delete" onClick={(e) => {
         e.stopPropagation(); 
         onDeleteCourse()
      }}>
        Delete
      </MenuItem>
    </DropdownMenu>
  );
};

interface HideCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  processing?: boolean;
}

export const HideCourseModal: React.FC<HideCourseModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  processing
}) => {
  return (
    <Modal
      title={null}
      open={isOpen}
      footer={null}
      onCancel={onClose}
      width={400}
      centered
      closable={false}
    >
      <ModalContent>
        <ModalIcon />
        <ModalTitle>Hide this course?</ModalTitle>
        <ModalDescription>
          Hiding this course will make it temporarily invisible to users. You
          can make it visible again anytime.
        </ModalDescription>
        <ModalFooter>
          <AppButton variant="secondary" onClick={onClose}>
            No
          </AppButton>
          <AppButton variant="primary" onClick={onConfirm} loading={processing}>
            Yes, hide
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface DeleteCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isQuestion?: boolean;
  processing?: boolean;
}

export const DeleteCourseModal: React.FC<DeleteCourseModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isQuestion = false, processing
}) => {
  return (
    <Modal
      title={null}
      open={isOpen}
      footer={null}
      onCancel={onClose}
      width={400}
      centered
      closable={false}
    >
      <ModalContent>
        <ModalIcon />
        <ModalTitle>
          Are you sure you want to delete{" "}
          {isQuestion ? "past questions" : "course"}?
        </ModalTitle>
        <ModalDescription>
          Once deleted, it will be permanently removed and cannot be recovered.
          This action is irreversible.
        </ModalDescription>
        <ModalFooter>
          <AppButton variant="secondary" onClick={onClose}>
            No
          </AppButton>
          <AppButton variant="delete" onClick={onConfirm} loading={processing}>
            Yes, delete
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
